import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Search, Sliders, ChevronUp, ChevronDown, Plus, MoreHorizontal, Trash2, X, Check, Download, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight } from 'react-feather'
import StatusBadge from './StatusBadge'
import { useNavigation } from '../../Context/NavigationContext'
import { useData } from '../../Context/DataContext'
import { TableSkeleton } from '../Loaders/Loader'

interface ColumnConfig {
  key: string
  label: string
  type?: 'text' | 'status' | 'badge' | 'date' | 'action' | 'assignee' | 'likelihood' | 'percent'
  width?: string
  badgeType?: 'status' | 'range' | 'facility' | 'insurance' | 'network' | 'claim_status'
  photoKey?: string
  filterable?: boolean
  hideOnMobile?: boolean
  primaryOnMobile?: boolean
}

interface Tab {
  label: string
  value: string
  count?: number
}

interface ModernTableProps {
  title?: string
  tabs?: Tab[]
  columns: ColumnConfig[]
  data: any[]
  searchPlaceholder?: string
  onAddNew?: () => void
  addButtonLabel?: string
  onRowClick?: (row: any) => void
  showRowNumbers?: boolean
  tabFilterKey?: string
  tabFilterFn?: (row: any, tabValue: string) => boolean
  getTabCount?: (tabValue: string, data: any[]) => number
  userRole?: string
  onDeleteRow?: (row: any) => void
  selectedRow?: any
  onExport?: () => void
  onCallSheet?: () => void
  pageSize?: number
  defaultSort?: { key: string; direction: 'asc' | 'desc' }
  /** Show shimmer skeleton rows in place of data/empty state while data is being fetched. */
  isLoading?: boolean
  /** Number of skeleton rows to render while loading. Defaults to pageSize or 8. */
  skeletonRows?: number
}

// Get initials from name for avatar
const getInitials = (name: string) => {
  if (!name || name === '-') return '?'
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

// Avatar component for assignee with photo support
const AssigneeAvatar = ({ name, photo }: { name: string, photo?: string | null }) => {
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        className="w-6 h-6 rounded-full object-cover flex-shrink-0"
      />
    )
  }
  return (
    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center flex-shrink-0">
      <span className="text-white text-[10px] font-semibold">{getInitials(name)}</span>
    </div>
  )
}

const ModernTableComponent: React.FC<ModernTableProps> = ({
  title,
  tabs,
  columns,
  data,
  searchPlaceholder = 'Search...',
  onAddNew,
  addButtonLabel = 'Add New',
  onRowClick,
  showRowNumbers = true,
  tabFilterKey,
  tabFilterFn,
  getTabCount,
  userRole,
  onDeleteRow,
  selectedRow,
  onExport,
  onCallSheet,
  pageSize,
  defaultSort,
  isLoading = false,
  skeletonRows,
}) => {
  const { isMobile } = useNavigation()
  const { currentTimeZone } = useData()
  const [activeTab, setActiveTab] = useState(tabs?.[0]?.value || 'all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(defaultSort || null)
  const [currentPage, setCurrentPage] = useState(0)
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({})
  const [filterSearchTerm, setFilterSearchTerm] = useState('')

  const getRecordId = (record: any) => {
    return record.intake_id || record.claim_id || record.userid || record.id || null
  }

  // Use selectedRow from parent if provided
  const selectedRowId = selectedRow ? getRecordId(selectedRow) : null

  const handleRowSelect = (row: any) => {
    onRowClick?.(row)
  }

  const menuRef = useRef<HTMLDivElement>(null)
  const sortRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLDivElement>(null)
  const tableTopRef = useRef<HTMLDivElement>(null)

  const isDevUser = userRole === 'dev'

  // Reset page when filters change
  useEffect(() => { setCurrentPage(0) }, [activeTab, searchTerm, columnFilters])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuIndex(null)
      }
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false)
      }
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter data based on tab selection
  const tabFilteredData = React.useMemo(() => {
    if (activeTab === 'all') return data

    if (tabFilterFn) {
      return data.filter(row => tabFilterFn(row, activeTab))
    }

    if (tabFilterKey) {
      return data.filter(row => {
        const value = String(row[tabFilterKey] || '').toLowerCase()
        return value.includes(activeTab.toLowerCase())
      })
    }

    return data
  }, [data, activeTab, tabFilterKey, tabFilterFn])

  // Filter data based on search and column filters
  const filteredData = useMemo(() => {
    return tabFilteredData.filter(row => {
      // Search filter
      if (searchTerm) {
        const matchesSearch = Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
        if (!matchesSearch) return false
      }

      // Column filters
      for (const [columnKey, selectedValues] of Object.entries(columnFilters)) {
        if (selectedValues.length === 0) continue
        const cellValue = String(row[columnKey] || '').toLowerCase()
        const matchesFilter = selectedValues.some(filterValue =>
          cellValue.includes(filterValue.toLowerCase())
        )
        if (!matchesFilter) return false
      }

      return true
    })
  }, [tabFilteredData, searchTerm, columnFilters])

  // Get unique values for each filterable column
  const filterOptions = useMemo(() => {
    const options: Record<string, string[]> = {}
    columns.forEach(column => {
      if (column.type === 'action') return
      const uniqueValues = new Set<string>()
      data.forEach(row => {
        const value = row[column.key]
        if (value && value !== '' && value !== '-' && value !== 'N/A') {
          uniqueValues.add(String(value))
        }
      })
      options[column.key] = Array.from(uniqueValues).sort()
    })
    return options
  }, [data, columns])

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(columnFilters).reduce((count, values) => count + values.length, 0)
  }, [columnFilters])

  // Get all active filters as array for badges
  const activeFilterBadges = useMemo(() => {
    const badges: { columnKey: string; columnLabel: string; value: string }[] = []
    for (const [columnKey, values] of Object.entries(columnFilters)) {
      const column = columns.find(c => c.key === columnKey)
      const columnLabel = column?.label || columnKey
      values.forEach(value => {
        badges.push({ columnKey, columnLabel, value })
      })
    }
    return badges
  }, [columnFilters, columns])

  // Remove a single filter
  const removeFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => {
      const current = prev[columnKey] || []
      const updated = current.filter(v => v !== value)
      if (updated.length === 0) {
        const { [columnKey]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [columnKey]: updated }
    })
  }

  // Toggle a filter value
  const toggleFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => {
      const current = prev[columnKey] || []
      if (current.includes(value)) {
        return { ...prev, [columnKey]: current.filter(v => v !== value) }
      } else {
        return { ...prev, [columnKey]: [...current, value] }
      }
    })
  }

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({})
  }

  // Clear sort
  const clearSort = () => {
    setSortConfig(null)
  }

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!sortConfig) return filteredData

    // Find the column config to determine the type
    const column = columns.find(col => col.key === sortConfig.key)
    const isDateColumn = column?.type === 'date'

    return [...filteredData].sort((a, b) => {
      let aVal = a[sortConfig.key]
      let bVal = b[sortConfig.key]

      // Handle date columns - parse as dates for proper sorting
      if (isDateColumn) {
        const aDate = aVal ? new Date(aVal).getTime() : 0
        const bDate = bVal ? new Date(bVal).getTime() : 0
        if (aDate < bDate) return sortConfig.direction === 'asc' ? -1 : 1
        if (aDate > bDate) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      }

      // Handle null/undefined values - push them to the end
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1

      // Default string/number comparison
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredData, sortConfig, columns])

  // Pagination
  const totalPages = pageSize ? Math.ceil(sortedData.length / pageSize) : 1
  const paginatedData = pageSize ? sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : sortedData

  const scrollToTop = () => {
    tableTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const goToPage = (page: number, shouldScroll = false) => {
    setCurrentPage(page)
    if (shouldScroll) scrollToTop()
  }

  const renderPagination = (scrollOnChange = false) => {
    if (!pageSize || totalPages <= 1) return null
    return (
      <div className="px-4 md:px-6 py-2.5 flex items-center justify-between">
        <span className="text-sm text-gray-400">
          {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, sortedData.length)} of {sortedData.length.toLocaleString()}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(0, scrollOnChange)}
            disabled={currentPage === 0}
            className="p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-dark-card-hover"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => goToPage(Math.max(0, currentPage - 1), scrollOnChange)}
            disabled={currentPage === 0}
            className="p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-dark-card-hover"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-1 text-sm text-white">
            {currentPage + 1} / {totalPages}
          </span>
          <button
            onClick={() => goToPage(Math.min(totalPages - 1, currentPage + 1), scrollOnChange)}
            disabled={currentPage >= totalPages - 1}
            className="p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-dark-card-hover"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => goToPage(totalPages - 1, scrollOnChange)}
            disabled={currentPage >= totalPages - 1}
            className="p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-dark-card-hover"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const renderCellContent = (row: any, column: ColumnConfig, forMobileCard: boolean = false) => {
    const value = row[column.key]

    switch (column.type) {
      case 'status':
      case 'badge':
        return <StatusBadge status={value} type={column.badgeType || 'status'} />
      case 'date':
        if (!value) return <span className="text-gray-500">-</span>
        try {
          // Parse date - handle both MM-DD-YYYY (from backend) and YYYY-MM-DD formats
          let date: Date
          if (typeof value === 'string') {
            // Check for MM-DD-YYYY format (from backend after timezone conversion)
            const mmddyyyyMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/)
            if (mmddyyyyMatch) {
              const [, mm, dd, yyyy] = mmddyyyyMatch
              // Create date at noon local time to avoid timezone day-shift issues
              date = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), 12, 0, 0)
            } else {
              // Try YYYY-MM-DD or other formats
              const yyyymmddMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
              if (yyyymmddMatch) {
                const [, yyyy, mm, dd] = yyyymmddMatch
                date = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), 12, 0, 0)
              } else {
                date = new Date(value)
              }
            }
          } else {
            date = new Date(value)
          }

          if (isNaN(date.getTime())) {
            return <span className="text-gray-400">{value}</span>
          }

          // Get current time in user's selected timezone for accurate comparison
          const timezone = currentTimeZone || 'America/Los_Angeles'
          const now = new Date()
          const nowInTimezone = new Date(now.toLocaleString('en-US', { timeZone: timezone }))

          // Convert the UTC date from DB to user's timezone
          const dateInTimezone = new Date(date.toLocaleString('en-US', { timeZone: timezone }))

          // Calculate difference based on dates only (not time) to avoid timezone hour discrepancies
          const todayStart = new Date(nowInTimezone.getFullYear(), nowInTimezone.getMonth(), nowInTimezone.getDate())
          const dateStart = new Date(dateInTimezone.getFullYear(), dateInTimezone.getMonth(), dateInTimezone.getDate())

          const diffTime = todayStart.getTime() - dateStart.getTime()
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

          if (forMobileCard) {
            // Shorter format for mobile
            if (diffDays <= 0) {
              return <span className="text-gray-400 text-xs">Today</span>
            } else if (diffDays === 1) {
              return <span className="text-gray-400 text-xs">Yesterday</span>
            } else if (diffDays < 7) {
              return <span className="text-gray-400 text-xs">{diffDays}d ago</span>
            } else {
              return <span className="text-gray-400 text-xs">{Math.floor(diffDays / 7)}w ago</span>
            }
          }

          if (diffDays <= 0) {
            return <span className="text-gray-300">Today</span>
          } else if (diffDays === 1) {
            return <span className="text-gray-300">Yesterday</span>
          } else if (diffDays < 7) {
            return <span className="text-gray-300">{diffDays} days ago</span>
          } else if (diffDays < 30) {
            return <span className="text-gray-300">{Math.floor(diffDays / 7)} weeks ago</span>
          } else {
            return <span className="text-gray-300">{Math.floor(diffDays / 30)} months ago</span>
          }
        } catch {
          return <span className="text-gray-400">{value}</span>
        }
      case 'action':
        return (
          <button className="p-1.5 hover:bg-dark-card-hover rounded-lg transition-colors">
            <MoreHorizontal className="w-4 h-4 text-gray-400" />
          </button>
        )
      case 'assignee':
        if (!value || value === '-') {
          return <span className="text-gray-500">-</span>
        }
        const photoUrl = column.photoKey ? row[column.photoKey] : null
        const coordinatorUsers = row.coordinatorUsers || []
        const coordinatorCount = row.coordinatorCount || 1

        // Multiple coordinators - show clustered avatars
        if (coordinatorCount > 1 && coordinatorUsers.length > 1) {
          return (
            <div className="flex items-center">
              <div className="flex -space-x-2">
                {coordinatorUsers.slice(0, 3).map((user: any, index: number) => (
                  <div key={user.userid || index} className="relative" style={{ zIndex: 3 - index }}>
                    <AssigneeAvatar name={user.name} photo={user.profile_photo} />
                  </div>
                ))}
                {coordinatorCount > 3 && (
                  <div className="w-6 h-6 rounded-full bg-dark-input border border-dark-border flex items-center justify-center">
                    <span className="text-[10px] text-gray-400">+{coordinatorCount - 3}</span>
                  </div>
                )}
              </div>
            </div>
          )
        }

        // Single coordinator
        return (
          <div className="flex items-center gap-2">
            <AssigneeAvatar name={value} photo={photoUrl} />
            <span className={`text-gray-300 truncate ${forMobileCard ? 'text-sm' : ''}`}>{value}</span>
          </div>
        )
      case 'likelihood': {
        if (!value || value === '—') return <span className="text-gray-500">—</span>
        const likelihoodStyles: Record<string, string> = {
          'highly likely': 'bg-green-500/15 text-green-400 border-green-500/30',
          'likely': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
          'unlikely': 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
          'very unlikely': 'bg-red-500/15 text-red-400 border-red-500/30',
        }
        const style = likelihoodStyles[String(value).toLowerCase()] || 'bg-gray-500/15 text-gray-400 border-gray-500/30'
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${style}`}>
            {value}
          </span>
        )
      }
      case 'percent': {
        if (!value || value === '—') return <span className="text-gray-500">—</span>
        const num = parseFloat(String(value).replace(/%/g, '')) || 0
        const pctColor = num >= 60 ? 'text-green-400' : num >= 30 ? 'text-yellow-400' : 'text-red-400'
        return <span className={`${pctColor} font-medium text-sm`}>{value}</span>
      }
      default:
        return (
          <span
            className={`text-gray-300 block truncate ${forMobileCard ? 'text-sm' : ''}`}
            title={value || ''}
          >
            {value || '-'}
          </span>
        )
    }
  }

  // Get columns for mobile card display
  const getMobileColumns = () => {
    const primary = columns.find(c => c.primaryOnMobile) || columns[0]
    const secondary = columns.filter(c => !c.hideOnMobile && c.key !== primary.key).slice(0, 3)
    return { primary, secondary }
  }

  // Mobile Card Component
  const MobileCard = ({ row, rowIndex }: { row: any; rowIndex: number }) => {
    const recordId = getRecordId(row)
    const isSelected = selectedRowId === recordId
    const { primary, secondary } = getMobileColumns()

    return (
      <div
        onClick={() => handleRowSelect(row)}
        className={`p-4 border-b border-dark-border/50 transition-all duration-200 active:bg-dark-card-hover ${
          onRowClick ? 'cursor-pointer' : ''
        } ${
          isSelected
            ? 'bg-accent-purple/20 border-l-4 border-l-accent-purple'
            : ''
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Primary field (e.g., name) */}
            <div className="font-medium text-white mb-1">
              {renderCellContent(row, primary, true)}
            </div>
            {/* Secondary fields */}
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {secondary.map((col) => (
                <div key={col.key} className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500">{col.label}:</span>
                  <span className="text-xs">{renderCellContent(row, col, true)}</span>
                </div>
              ))}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
        </div>
      </div>
    )
  }

  return (
    <div ref={tableTopRef} className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
      {/* Tabs - Scrollable on mobile */}
      {tabs && tabs.length > 0 && (
        <div className="px-3 md:px-6 border-b border-dark-border overflow-x-auto">
          <div className="flex items-center justify-between min-w-max md:min-w-0">
            <div className="flex items-center gap-3 md:gap-6">
              {tabs.map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.value
                      ? tab.value === 'yellow_stripe'
                        ? 'text-yellow-400 border-yellow-400'
                        : 'text-accent-teal border-accent-teal'
                      : tab.value === 'yellow_stripe'
                        ? 'text-yellow-400/60 border-transparent hover:text-yellow-300'
                        : 'text-gray-400 border-transparent hover:text-white'
                  }`}
                >
                  {tab.label}
                  {(tab.count !== undefined || getTabCount) && (
                    <span className="ml-1 md:ml-2 text-xs text-gray-500">
                      ({getTabCount ? getTabCount(tab.value, data) : tab.count})
                    </span>
                  )}
                </button>
              ))}
            </div>
            {(onCallSheet || onExport) && !isMobile && (
              <div className="flex items-center gap-1.5 ml-2">
                {onCallSheet && (
                  <button
                    onClick={onCallSheet}
                    className="px-3 py-1.5 bg-accent-purple text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
                  >
                    Call Report
                  </button>
                )}
                {onExport && (
                  <button
                    onClick={onExport}
                    className="px-3 py-1.5 bg-accent-teal text-black text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
                  >
                    Export
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Bar - Responsive */}
      <div className="px-3 md:px-6 py-3 border-b border-dark-border">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-3 flex-wrap md:flex-nowrap">
            {/* Search - Full width on mobile */}
            <div className="flex items-center gap-2 px-3 py-2 bg-dark-input border border-dark-border rounded-xl flex-1 md:flex-initial md:min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent text-white text-sm placeholder-gray-500 focus:outline-none flex-1 min-w-0"
              />
              {searchTerm && (
                <X
                  className="w-4 h-4 text-gray-400 cursor-pointer flex-shrink-0"
                  onClick={() => setSearchTerm('')}
                />
              )}
            </div>

            {/* Sort & Filter buttons */}
            <div className="flex items-center gap-2">
              {/* Sort Button & Dropdown */}
              <div className="relative" ref={sortRef}>
                <button
                  onClick={() => {
                    setShowSortDropdown(!showSortDropdown)
                    setShowFilterDropdown(false)
                  }}
                  className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-xl transition-colors ${
                    sortConfig
                      ? 'text-accent-teal bg-accent-teal/10 border border-accent-teal/30'
                      : 'text-gray-400 hover:text-white hover:bg-dark-card-hover active:bg-dark-card-hover'
                  }`}
                >
                  <div className="flex flex-col -space-y-2">
                    <ChevronUp className="w-3 h-3" />
                    <ChevronDown className="w-3 h-3" />
                  </div>
                  <span className="text-sm hidden md:inline">Sort</span>
                  {sortConfig && (
                    <X
                      className="w-3 h-3 ml-1 hover:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation()
                        clearSort()
                      }}
                    />
                  )}
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full left-0 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-2xl z-50 min-w-[220px] overflow-hidden">
                    <div className="px-4 py-2 border-b border-dark-border">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Sort by</span>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {columns.filter(col => col.type !== 'action').map(column => (
                        <div key={column.key} className="border-b border-dark-border/50 last:border-0">
                          <button
                            onClick={() => {
                              setSortConfig({ key: column.key, direction: 'asc' })
                              setShowSortDropdown(false)
                            }}
                            className={`w-full px-4 py-2.5 md:py-2 text-left text-sm flex items-center justify-between transition-colors ${
                              sortConfig?.key === column.key && sortConfig.direction === 'asc'
                                ? 'bg-accent-teal/10 text-accent-teal'
                                : 'text-gray-300 hover:bg-dark-card-hover'
                            }`}
                          >
                            <span>{column.label} (A-Z)</span>
                            {sortConfig?.key === column.key && sortConfig.direction === 'asc' && (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setSortConfig({ key: column.key, direction: 'desc' })
                              setShowSortDropdown(false)
                            }}
                            className={`w-full px-4 py-2.5 md:py-2 text-left text-sm flex items-center justify-between transition-colors ${
                              sortConfig?.key === column.key && sortConfig.direction === 'desc'
                                ? 'bg-accent-teal/10 text-accent-teal'
                                : 'text-gray-300 hover:bg-dark-card-hover'
                            }`}
                          >
                            <span>{column.label} (Z-A)</span>
                            {sortConfig?.key === column.key && sortConfig.direction === 'desc' && (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Filter Button & Dropdown */}
              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => {
                    setShowFilterDropdown(!showFilterDropdown)
                    setShowSortDropdown(false)
                  }}
                  className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-xl transition-colors ${
                    activeFilterCount > 0
                      ? 'text-accent-teal bg-accent-teal/10 border border-accent-teal/30'
                      : 'text-gray-400 hover:text-white hover:bg-dark-card-hover active:bg-dark-card-hover'
                  }`}
                >
                  <Sliders className="w-4 h-4" />
                  <span className="text-sm hidden md:inline">Filter</span>
                  {activeFilterCount > 0 && (
                    <>
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-accent-teal/20 rounded-full">
                        {activeFilterCount}
                      </span>
                      <X
                        className="w-3 h-3 ml-1 hover:text-red-400"
                        onClick={(e) => {
                          e.stopPropagation()
                          clearAllFilters()
                        }}
                      />
                    </>
                  )}
                </button>

                {showFilterDropdown && (
                  <div className="absolute top-full right-0 md:left-0 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-2xl z-50 min-w-[280px] max-w-[90vw] overflow-hidden">
                    <div className="px-4 py-2 border-b border-dark-border flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Filter by</span>
                      {activeFilterCount > 0 && (
                        <button
                          onClick={clearAllFilters}
                          className="text-xs text-red-400 hover:text-red-300 transition-colors"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    {/* Filter Search */}
                    <div className="px-3 py-2 border-b border-dark-border">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-dark-input border border-dark-border rounded-lg">
                        <Search className="w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search filters..."
                          value={filterSearchTerm}
                          onChange={(e) => setFilterSearchTerm(e.target.value)}
                          className="bg-transparent text-white text-sm placeholder-gray-500 focus:outline-none flex-1"
                        />
                        {filterSearchTerm && (
                          <X
                            className="w-3.5 h-3.5 text-gray-400 hover:text-white cursor-pointer"
                            onClick={() => setFilterSearchTerm('')}
                          />
                        )}
                      </div>
                    </div>
                    <div className="max-h-[350px] overflow-y-auto">
                      {columns.filter(col => col.type !== 'action').map(column => {
                        const options = filterOptions[column.key] || []
                        if (options.length === 0) return null
                        const selectedValues = columnFilters[column.key] || []

                        // Filter options based on search term
                        const filteredOptions = filterSearchTerm
                          ? options.filter(opt =>
                              opt.toLowerCase().includes(filterSearchTerm.toLowerCase()) ||
                              column.label.toLowerCase().includes(filterSearchTerm.toLowerCase())
                            )
                          : options

                        if (filteredOptions.length === 0 && !filterSearchTerm) return null
                        if (filteredOptions.length === 0) return null

                        return (
                          <div key={column.key} className="border-b border-dark-border/50 last:border-0">
                            <div className="px-4 py-2 bg-dark-input/50">
                              <span className="text-xs font-medium text-gray-400">{column.label}</span>
                            </div>
                            <div className="max-h-[150px] overflow-y-auto">
                              {filteredOptions.slice(0, 15).map(value => (
                                <button
                                  key={value}
                                  onClick={() => toggleFilter(column.key, value)}
                                  className={`w-full px-4 py-2.5 md:py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                                    selectedValues.includes(value)
                                      ? 'bg-accent-teal/10 text-accent-teal'
                                      : 'text-gray-300 hover:bg-dark-card-hover active:bg-dark-card-hover'
                                  }`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                    selectedValues.includes(value)
                                      ? 'bg-accent-teal border-accent-teal'
                                      : 'border-gray-500'
                                  }`}>
                                    {selectedValues.includes(value) && (
                                      <Check className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  <span className="truncate">{value}</span>
                                </button>
                              ))}
                              {filteredOptions.length > 15 && (
                                <div className="px-4 py-2 text-xs text-gray-500">
                                  +{filteredOptions.length - 15} more options
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* View Dropdown - Hide on mobile */}
            <button className="hidden md:flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-dark-card-hover rounded-xl transition-colors">
              <span className="text-sm">List View</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {/* Add Button */}
            {onAddNew && (
              <button
                onClick={onAddNew}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-gradient-to-r from-accent-teal to-accent-deep-purple text-white text-sm font-medium rounded-xl hover:opacity-90 active:opacity-80 transition-opacity"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden md:inline">{addButtonLabel}</span>
                <span className="md:hidden">Add</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Active Filter Badges - Scrollable on mobile */}
      {activeFilterBadges.length > 0 && (
        <div className="px-3 md:px-6 py-2 border-b border-dark-border bg-dark-input/30 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            <span className="text-xs text-gray-500 mr-1 flex-shrink-0">Filters:</span>
            {activeFilterBadges.map((badge, index) => (
              <span
                key={`${badge.columnKey}-${badge.value}-${index}`}
                className="inline-flex items-center gap-1 md:gap-1.5 px-2 md:px-2.5 py-1 bg-accent-teal/15 text-accent-teal text-xs rounded-full border border-accent-teal/30"
              >
                <span className="text-gray-400 hidden md:inline">{badge.columnLabel}:</span>
                <span className="font-medium truncate max-w-[100px] md:max-w-[150px]">{badge.value}</span>
                <X
                  className="w-3 h-3 hover:text-red-400 cursor-pointer flex-shrink-0"
                  onClick={() => removeFilter(badge.columnKey, badge.value)}
                />
              </span>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-xs text-gray-400 hover:text-red-400 ml-2 transition-colors flex-shrink-0"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Top Pagination */}
      {pageSize && totalPages > 1 && (
        <div className="border-b border-dark-border">
          {renderPagination(false)}
        </div>
      )}

      {/* Mobile Card View */}
      {isMobile ? (
        <div className="divide-y divide-dark-border/50">
          {isLoading ? (
            <TableSkeleton
              mode="cards"
              rows={skeletonRows ?? pageSize ?? 6}
            />
          ) : paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => (
              <MobileCard key={rowIndex} row={row} rowIndex={currentPage * (pageSize || 0) + rowIndex} />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <p className="text-lg">No records found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-border">
                {showRowNumbers && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-12">
                    #
                  </th>
                )}
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    style={{ minWidth: column.width }}
                    onClick={() => handleSort(column.key)}
                  >
                    <div className="flex items-center gap-1">
                      {column.label}
                      {sortConfig?.key === column.key && (
                        <span className="text-accent-teal">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {isDevUser && <th className="px-6 py-3 w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <TableSkeleton
                  rows={skeletonRows ?? pageSize ?? 8}
                  columns={columns.length + (isDevUser ? 1 : 0)}
                  showRowNumberColumn={showRowNumbers}
                />
              )}
              {!isLoading && paginatedData.map((row, rowIndex) => {
                const recordId = getRecordId(row)
                const isSelected = selectedRowId === recordId
                const globalIndex = currentPage * (pageSize || 0) + rowIndex
                return (
                <tr
                  key={rowIndex}
                  onClick={() => handleRowSelect(row)}
                  className={`border-b border-dark-border/50 transition-all duration-200 ${
                    onRowClick ? 'cursor-pointer' : ''
                  } ${
                    isSelected
                      ? 'bg-accent-purple/20 border-l-4 border-l-accent-purple ring-1 ring-accent-purple/30'
                      : 'hover:bg-dark-card-hover'
                  }`}
                >
                  {showRowNumbers && (
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {globalIndex + 1}
                    </td>
                  )}
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className="px-6 py-4 text-sm"
                      style={{ maxWidth: column.width, overflow: 'hidden' }}
                    >
                      {renderCellContent(row, column)}
                    </td>
                  ))}
                  {isDevUser && (
                    <td className="px-6 py-4 relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpenMenuIndex(openMenuIndex === rowIndex ? null : rowIndex)
                        }}
                        className="p-1.5 hover:bg-dark-input rounded-lg transition-colors"
                      >
                        <MoreHorizontal className="w-4 h-4 text-gray-400" />
                      </button>
                      {openMenuIndex === rowIndex && (
                        <div
                          ref={menuRef}
                          className="absolute right-0 top-full mt-1 bg-dark-card border border-dark-border rounded-xl shadow-xl z-50 overflow-hidden min-w-[120px]"
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteRow?.(row)
                              setOpenMenuIndex(null)
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
                )
              })}
            </tbody>
          </table>

          {!isLoading && paginatedData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <p className="text-lg">No records found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      )}

      {/* Bottom Pagination - scrolls to top on click */}
      {pageSize && totalPages > 1 && (
        <div className="border-t border-dark-border">
          {renderPagination(true)}
        </div>
      )}
    </div>
  )
}

export default ModernTableComponent
